import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Download, FileText, AlertTriangle, CheckCircle2, Clock, HelpCircle, ChevronDown, ChevronRight, MessageSquare, Sparkles, Video, Info, Copy } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { BatchPdpMockModal } from './BatchPdpMockModal';

// Copy helper — tries the async Clipboard API and falls back to the legacy
// execCommand so the popover works in every browsing context we run in
// (preview iframes, incognito with permission prompts, older Safari, etc.).
const copyToClipboard = async (text) => {
  const legacyCopy = () => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (!ok) throw new Error('execCommand copy returned false');
  };
  try {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // Permission denied / not focused — try the legacy path before giving up
        legacyCopy();
      }
    } else {
      legacyCopy();
    }
    toast.success('Link copied');
  } catch {
    toast.error('Copy failed — please copy manually');
  }
};

// ── Helpers ────────────────────────────────────────────────
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a, b) => {
  try {
    const d1 = new Date(a + 'T00:00:00');
    const d2 = new Date(b + 'T00:00:00');
    return Math.round((d2 - d1) / 86400000);
  } catch { return 0; }
};
const isWeekend = (bt) => (bt || '').toLowerCase().includes('weekend');
const AT_COMPLETION_PCT = 90;

// Classify a batch into one status bucket for filtering.
// Priority: past_end > at_completion > not_tracked > on_track
function classify(b) {
  const today = todayIso();
  const finished = ['Completed', 'Cancelled'].includes(b.batch_status);
  const pastEnd = b.end_date && b.end_date < today && !finished;
  if (pastEnd) return 'past_end';
  if (b.topic_completion_pct != null && b.topic_completion_pct >= AT_COMPLETION_PCT) return 'at_completion';
  if (b.topic_completion_pct == null) return 'not_tracked';
  return 'on_track';
}

const STATUS_META = {
  past_end:      { label: 'Beyond End Date',    Icon: AlertTriangle,  color: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200' },
  at_completion: { label: 'At / Near Completion', Icon: CheckCircle2, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  on_track:      { label: 'On Track',           Icon: Clock,          color: 'text-sky-700',     bg: 'bg-sky-50',     border: 'border-sky-200' },
  not_tracked:   { label: 'Not Tracked',        Icon: HelpCircle,     color: 'text-slate-600',   bg: 'bg-slate-50',   border: 'border-slate-200' },
};

// True when neither the CRM link nor a manual override exists
const hasNoWhatsapp = (b) => !((b.whatsapp_link_crm && b.whatsapp_link_crm.trim()) || (b.whatsapp_link_manual && b.whatsapp_link_manual.trim()));

// ── Progress bar ───────────────────────────────────────────
const PctBar = ({ value }) => {
  if (value == null) return <span className="text-[10px] text-slate-400 italic">— not tracked</span>;
  const color = value >= AT_COMPLETION_PCT ? 'bg-emerald-500' : value > 0 ? 'bg-amber-400' : 'bg-slate-300';
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-slate-600 w-8">{value}%</span>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status];
  if (!m) return null;
  const { Icon, color, bg, border } = m;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${color} ${bg} border ${border} rounded-full px-1.5 py-0.5`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};

// ── Main modal ─────────────────────────────────────────────
export const BatchProgressModal = ({ open, onOpenChange, tableData }) => {
  const [typeFilter, setTypeFilter] = useState('all'); // all | weekday | weekend
  const [statusFilter, setStatusFilter] = useState('all'); // all | past_end | at_completion | on_track | not_tracked
  const [groupBy, setGroupBy] = useState('trainer'); // trainer | none
  const [expanded, setExpanded] = useState({}); // {trainer_name: true} — undefined means collapsed by default
  const [pctMin, setPctMin] = useState(''); // '' means unbounded
  const [pctMax, setPctMax] = useState('');
  const [includeUntracked, setIncludeUntracked] = useState(true);
  const [missingWhatsapp, setMissingWhatsapp] = useState(false);
  const [missingPdp, setMissingPdp] = useState(false);
  const [missingMock, setMissingMock] = useState(false);
  const [missingLink, setMissingLink] = useState(false);
  const [fillBatch, setFillBatch] = useState(null);

  // Flatten all batches from tableData with trainer_name attached.
  const allBatches = useMemo(() => {
    const rows = [];
    (tableData || []).forEach((t) => {
      (t.batches || []).forEach((b) => rows.push({ ...b, trainer_name: t.trainer_name }));
    });
    return rows;
  }, [tableData]);

  const filtered = useMemo(() => {
    const lo = pctMin === '' ? -Infinity : Number(pctMin);
    const hi = pctMax === '' ? Infinity : Number(pctMax);
    return allBatches.filter((b) => {
      if (typeFilter === 'weekday' && isWeekend(b.batch_type)) return false;
      if (typeFilter === 'weekend' && !isWeekend(b.batch_type)) return false;
      if (statusFilter !== 'all' && classify(b) !== statusFilter) return false;
      if (missingWhatsapp && !hasNoWhatsapp(b)) return false;
      if (missingPdp && !(b.missing_pdp_count > 0)) return false;
      if (missingMock && !(b.missing_mock_count > 0)) return false;
      if (missingLink && !(b.missing_link_count > 0)) return false;
      // % completion range — includeUntracked keeps null values visible;
      // otherwise nulls are dropped when a range is active.
      const p = b.topic_completion_pct;
      if (p == null) {
        const rangeActive = pctMin !== '' || pctMax !== '';
        if (rangeActive && !includeUntracked) return false;
      } else if (p < lo || p > hi) {
        return false;
      }
      return true;
    });
  }, [allBatches, typeFilter, statusFilter, pctMin, pctMax, includeUntracked, missingWhatsapp, missingPdp, missingMock, missingLink]);

  // Counts per status for header chips
  const counts = useMemo(() => {
    const base = { all: 0, past_end: 0, at_completion: 0, on_track: 0, not_tracked: 0 };
    const lo = pctMin === '' ? -Infinity : Number(pctMin);
    const hi = pctMax === '' ? Infinity : Number(pctMax);
    const rangeActive = pctMin !== '' || pctMax !== '';
    allBatches.forEach((b) => {
      if (typeFilter === 'weekday' && isWeekend(b.batch_type)) return;
      if (typeFilter === 'weekend' && !isWeekend(b.batch_type)) return;
      if (missingWhatsapp && !hasNoWhatsapp(b)) return;
      const p = b.topic_completion_pct;
      if (p == null) {
        if (rangeActive && !includeUntracked) return;
      } else if (p < lo || p > hi) {
        return;
      }
      base.all += 1;
      base[classify(b)] += 1;
    });
    return base;
  }, [allBatches, typeFilter, pctMin, pctMax, includeUntracked, missingWhatsapp]);

  // Aggregate PDP/Mock missing counts across all batches for chip labels
  const pdpMockCounts = useMemo(() => allBatches.reduce((acc, b) => {
    acc.pdp += Math.min(1, b.missing_pdp_count || 0);
    acc.mock += Math.min(1, b.missing_mock_count || 0);
    acc.link += Math.min(1, b.missing_link_count || 0);
    return acc;
  }, { pdp: 0, mock: 0, link: 0 }), [allBatches]);

  // Total batches missing WhatsApp link — shown on the filter chip
  const missingWhatsappCount = useMemo(() => allBatches.filter(hasNoWhatsapp).length, [allBatches]);

  // Group results by trainer (or single 'All Batches' bucket)
  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'All Batches', batches: filtered }];
    const m = new Map();
    filtered.forEach((b) => {
      const k = b.trainer_name || 'Unknown';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(b);
    });
    return Array.from(m.entries())
      .map(([key, batches]) => ({ key, batches: batches.sort((a, b) => String(a.batch_number).localeCompare(String(b.batch_number), undefined, { numeric: true })) }))
      .sort((a, b) => b.batches.length - a.batches.length);
  }, [filtered, groupBy]);

  const exportCsv = () => {
    const header = ['Trainer', 'Batch #', 'Batch Name', 'Course', 'Mode', 'Centre', 'Type', 'Status', 'Start', 'End', 'Days Overdue', 'Students', 'Topic %', 'WhatsApp (CRM)', 'WhatsApp (Manual)', 'Audit Trainer', 'Notes of Auditor', 'Progress'];
    const rows = filtered.map((b) => {
      const c = classify(b);
      const overdue = c === 'past_end' && b.end_date ? daysBetween(b.end_date, todayIso()) : '';
      return [
        b.trainer_name || '',
        b.batch_number || '',
        b.batch_name || '',
        b.course_name || '',
        b.training_mode || '',
        b.centre_name || '',
        isWeekend(b.batch_type) ? 'Weekend' : 'Weekday',
        b.batch_status || '',
        b.start_date || '',
        b.end_date || '',
        overdue,
        b.student_count ?? 0,
        b.topic_completion_pct ?? '',
        b.whatsapp_link_crm || '',
        b.whatsapp_link_manual || '',
        b.audit_trainer_name || '',
        b.notes_of_auditor || '',
        STATUS_META[c]?.label || '',
      ];
    });
    const csv = [header, ...rows]
      .map((r) => r.map((c) => {
        const s = String(c ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `batch-progress-${todayIso()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 32;

    // Header band
    doc.setFillColor(79, 70, 229); // violet-600
    doc.rect(0, 0, pageW, 54, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Batch Progress Report', margin, 26);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Trainer Dashboard · Completion & End-Date Tracker', margin, 42);
    const genOn = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    doc.text(`Generated: ${genOn}`, pageW - margin, 42, { align: 'right' });

    // Filter summary chips
    const chips = [
      `Type: ${typeFilter === 'all' ? 'All' : typeFilter[0].toUpperCase() + typeFilter.slice(1)}`,
      `Status: ${statusFilter === 'all' ? 'All' : (STATUS_META[statusFilter]?.label || statusFilter)}`,
      `Group: ${groupBy === 'trainer' ? 'Trainer' : 'Flat'}`,
    ];
    if (pctMin !== '' || pctMax !== '') {
      const lo = pctMin === '' ? '0' : pctMin;
      const hi = pctMax === '' ? '100' : pctMax;
      chips.push(`Topic %: ${lo}–${hi}${includeUntracked ? ' (incl. null)' : ''}`);
    }
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(9);
    doc.text(chips.join('   ·   '), margin, 74);

    // KPI strip
    const kpi = [
      { label: 'Total', v: counts.all, color: [30, 41, 59] },
      { label: 'Beyond End Date', v: counts.past_end, color: [190, 18, 60] },
      { label: 'At Completion', v: counts.at_completion, color: [5, 150, 105] },
      { label: 'On Track', v: counts.on_track, color: [2, 132, 199] },
      { label: 'Not Tracked', v: counts.not_tracked, color: [100, 116, 139] },
    ];
    const boxW = (pageW - 2 * margin - (kpi.length - 1) * 8) / kpi.length;
    const boxTop = 88;
    kpi.forEach((k, i) => {
      const x = margin + i * (boxW + 8);
      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, boxTop, boxW, 44, 4, 4, 'FD');
      doc.setTextColor(...k.color);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text(String(k.v), x + 10, boxTop + 24);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(k.label.toUpperCase(), x + 10, boxTop + 36);
    });

    // Body — grouped by trainer or flat
    let cursorY = boxTop + 60;
    const drawGroup = (title, batches) => {
      if (cursorY > pageH - 80) { doc.addPage(); cursorY = margin; }
      // Group heading pill
      doc.setFillColor(238, 242, 255);
      doc.setDrawColor(199, 210, 254);
      doc.roundedRect(margin, cursorY, pageW - 2 * margin, 22, 3, 3, 'FD');
      doc.setTextColor(67, 56, 202);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(title, margin + 10, cursorY + 15);
      const overdue = batches.filter((b) => classify(b) === 'past_end').length;
      const near = batches.filter((b) => classify(b) === 'at_completion').length;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(`${batches.length} batches · ${overdue} overdue · ${near} near done`,
        pageW - margin - 10, cursorY + 15, { align: 'right' });
      cursorY += 26;

      autoTable(doc, {
        startY: cursorY,
        margin: { left: margin, right: margin },
        head: [['#', 'Batch / Course', 'Mode', 'Type', 'Start', 'End', 'Overdue', 'Std', 'Topic %', 'Status']],
        body: batches.map((b) => {
          const c = classify(b);
          const overdueDays = c === 'past_end' && b.end_date ? daysBetween(b.end_date, todayIso()) : '';
          return [
            b.batch_number || '',
            `${b.batch_name || ''}\n${b.course_name || ''}`,
            b.training_mode || '—',
            isWeekend(b.batch_type) ? 'Weekend' : 'Weekday',
            b.start_date || '—',
            b.end_date || '—',
            overdueDays === '' ? '' : `+${overdueDays}d`,
            b.student_count ?? 0,
            b.topic_completion_pct == null ? '—' : `${b.topic_completion_pct}%`,
            STATUS_META[c]?.label || '',
          ];
        }),
        styles: { fontSize: 8, cellPadding: 4, valign: 'middle', overflow: 'linebreak' },
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontSize: 8, halign: 'left' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 32, halign: 'center', fontStyle: 'bold' },
          1: { cellWidth: 200 },
          2: { cellWidth: 60 },
          3: { cellWidth: 55 },
          4: { cellWidth: 60 },
          5: { cellWidth: 60 },
          6: { cellWidth: 44, halign: 'right', textColor: [190, 18, 60] },
          7: { cellWidth: 30, halign: 'right' },
          8: { cellWidth: 46, halign: 'right' },
          9: { cellWidth: 92 },
        },
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          const row = data.row.raw;
          // Row-level status accent on the last column
          if (data.column.index === 9) {
            const label = row[9];
            if (label === 'Beyond End Date') { data.cell.styles.textColor = [190, 18, 60]; data.cell.styles.fontStyle = 'bold'; }
            else if (label === 'At / Near Completion') { data.cell.styles.textColor = [5, 150, 105]; data.cell.styles.fontStyle = 'bold'; }
            else if (label === 'On Track') { data.cell.styles.textColor = [2, 132, 199]; }
            else if (label === 'Not Tracked') { data.cell.styles.textColor = [100, 116, 139]; }
          }
        },
      });
      cursorY = doc.lastAutoTable.finalY + 14;
    };

    if (groups.length === 0) {
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(11);
      doc.text('No batches match the current filters.', pageW / 2, cursorY + 40, { align: 'center' });
    } else {
      groups.forEach((g) => drawGroup(g.key, g.batches));
    }

    // Footer with page numbers
    const total = doc.getNumberOfPages();
    for (let i = 1; i <= total; i += 1) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Page ${i} of ${total}`, pageW - margin, pageH - 16, { align: 'right' });
      doc.text('Confidential · Trainer Dashboard', margin, pageH - 16);
    }

    doc.save(`batch-progress-${todayIso()}.pdf`);
  };

  const toggle = (k) => setExpanded((prev) => ({ ...prev, [k]: !(prev[k] === true) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="batch-progress-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            Batch Progress · Completion & End-Date Tracker
          </DialogTitle>
          <p className="text-[11px] text-slate-500 mt-0.5">Grouped by trainer · flags batches that have crossed their end date or are approaching completion (≥ {AT_COMPLETION_PCT}% topics done).</p>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-2">
          <div className="flex items-center gap-1" data-testid="bp-type-filter">
            {[
              { k: 'all', label: 'All' },
              { k: 'weekday', label: 'Weekday' },
              { k: 'weekend', label: 'Weekend' },
            ].map((o) => (
              <button key={o.k} onClick={() => setTypeFilter(o.k)}
                data-testid={`bp-type-${o.k}`}
                className={`text-[11px] px-2.5 py-1 rounded-md border transition ${typeFilter === o.k ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {o.label}
              </button>
            ))}
          </div>

          <div className="h-4 w-px bg-slate-200 mx-1" />

          {/* WhatsApp missing filter */}
          <button onClick={() => setMissingWhatsapp((v) => !v)}
            data-testid="bp-missing-whatsapp"
            title="Show only batches missing a WhatsApp Group Link (CRM + manual both empty)"
            className={`text-[11px] px-2.5 py-1 rounded-md border transition inline-flex items-center gap-1 ${
              missingWhatsapp ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}>
            <MessageSquare className={`w-3 h-3 ${missingWhatsapp ? 'text-white' : 'text-emerald-600'}`} />
            Missing WhatsApp
            <span className={`tabular-nums ${missingWhatsapp ? 'text-white/90' : 'text-slate-400'}`}>· {missingWhatsappCount}</span>
          </button>
          <button onClick={() => setMissingPdp((v) => !v)}
            data-testid="bp-missing-pdp"
            title="Show batches with at least one student missing PDP"
            className={`text-[11px] px-2.5 py-1 rounded-md border transition inline-flex items-center gap-1 ${
              missingPdp ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}>
            <Sparkles className={`w-3 h-3 ${missingPdp ? 'text-white' : 'text-rose-600'}`} />
            Missing PDP <span className={`tabular-nums ${missingPdp ? 'text-white/90' : 'text-slate-400'}`}>· {pdpMockCounts.pdp}</span>
          </button>
          <button onClick={() => setMissingMock((v) => !v)}
            data-testid="bp-missing-mock"
            title="Show batches with at least one student missing Mock Interview status"
            className={`text-[11px] px-2.5 py-1 rounded-md border transition inline-flex items-center gap-1 ${
              missingMock ? 'bg-amber-600 text-white border-amber-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}>
            <CheckCircle2 className={`w-3 h-3 ${missingMock ? 'text-white' : 'text-amber-600'}`} />
            Missing Mock <span className={`tabular-nums ${missingMock ? 'text-white/90' : 'text-slate-400'}`}>· {pdpMockCounts.mock}</span>
          </button>
          <button onClick={() => setMissingLink((v) => !v)}
            data-testid="bp-missing-link"
            title="Show batches with at least one student missing a Mock Interview Link"
            className={`text-[11px] px-2.5 py-1 rounded-md border transition inline-flex items-center gap-1 ${
              missingLink ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}>
            <Video className={`w-3 h-3 ${missingLink ? 'text-white' : 'text-violet-600'}`} />
            Missing Link <span className={`tabular-nums ${missingLink ? 'text-white/90' : 'text-slate-400'}`}>· {pdpMockCounts.link}</span>
          </button>

          <div className="h-4 w-px bg-slate-200 mx-1" />

          {/* Status chips with counts */}
          <div className="flex items-center gap-1 flex-wrap" data-testid="bp-status-filter">
            {[
              { k: 'all', label: 'All' },
              { k: 'past_end', label: 'Beyond End Date' },
              { k: 'at_completion', label: `At Completion (≥${AT_COMPLETION_PCT}%)` },
              { k: 'on_track', label: 'On Track' },
              { k: 'not_tracked', label: 'Not Tracked' },
            ].map((o) => {
              const active = statusFilter === o.k;
              const meta = STATUS_META[o.k];
              const activeCls = active
                ? (o.k === 'past_end' ? 'bg-rose-600 text-white border-rose-600'
                  : o.k === 'at_completion' ? 'bg-emerald-600 text-white border-emerald-600'
                  : o.k === 'on_track' ? 'bg-sky-600 text-white border-sky-600'
                  : o.k === 'not_tracked' ? 'bg-slate-600 text-white border-slate-600'
                  : 'bg-slate-800 text-white border-slate-800')
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50';
              return (
                <button key={o.k} onClick={() => setStatusFilter(o.k)}
                  data-testid={`bp-status-${o.k}`}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition inline-flex items-center gap-1 ${activeCls}`}>
                  {meta && <meta.Icon className={`w-3 h-3 ${active ? 'text-white' : meta.color}`} />}
                  {o.label}
                  <span className={`tabular-nums ${active ? 'text-white/90' : 'text-slate-400'}`}>· {counts[o.k]}</span>
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <label className="flex items-center gap-1 text-[10px] text-slate-500" title="Filter batches by Topic Completion %">
              <span>%</span>
              <input type="number" min="0" max="100" value={pctMin} onChange={(e) => setPctMin(e.target.value)} placeholder="min"
                data-testid="bp-pct-min"
                className="w-14 text-[10px] border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-400" />
              <span className="text-slate-400">→</span>
              <input type="number" min="0" max="100" value={pctMax} onChange={(e) => setPctMax(e.target.value)} placeholder="max"
                data-testid="bp-pct-max"
                className="w-14 text-[10px] border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-400" />
              {(pctMin !== '' || pctMax !== '') && (
                <label className="flex items-center gap-0.5 ml-1 select-none" title="Keep null/untracked batches visible">
                  <input type="checkbox" checked={includeUntracked} onChange={(e) => setIncludeUntracked(e.target.checked)}
                    data-testid="bp-pct-include-untracked" className="w-3 h-3 accent-violet-600" />
                  <span>incl. null</span>
                </label>
              )}
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500">
              Group:
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="bp-group-by"
                className="text-[10px] border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-400">
                <option value="trainer">Trainer</option>
                <option value="none">Flat list</option>
              </select>
            </label>
            {groupBy === 'trainer' && (
              <div className="flex items-center gap-0.5 text-[10px]">
                <button onClick={() => setExpanded(Object.fromEntries(groups.map((g) => [g.key, true])))}
                  data-testid="bp-expand-all"
                  className="px-1.5 py-0.5 border border-slate-200 rounded-md text-slate-600 bg-white hover:bg-slate-50">Expand all</button>
                <button onClick={() => setExpanded({})}
                  data-testid="bp-collapse-all"
                  className="px-1.5 py-0.5 border border-slate-200 rounded-md text-slate-600 bg-white hover:bg-slate-50">Collapse all</button>
              </div>
            )}
            <button onClick={exportCsv} data-testid="bp-export-csv"
              className="text-[11px] px-2.5 py-1 rounded-md border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex items-center gap-1">
              <Download className="w-3 h-3" /> CSV
            </button>
            <button onClick={exportPdf} data-testid="bp-export-pdf"
              className="text-[11px] px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 flex items-center gap-1">
              <FileText className="w-3 h-3" /> PDF
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="bp-body">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-xs text-slate-400">No batches match the current filters.</div>
          ) : (
            <div className="space-y-3">
              {groups.map((g) => {
                const key = g.key;
                const isOpen = expanded[key] === true; // default = collapsed
                return (
                  <div key={key} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`bp-group-${key}`}>
                    <button onClick={() => toggle(key)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-800">
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        {key}
                        <span className="text-[10px] text-slate-400 font-normal">· {g.batches.length} batch{g.batches.length === 1 ? '' : 'es'}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 tabular-nums">
                        {g.batches.filter((b) => classify(b) === 'past_end').length} overdue ·{' '}
                        {g.batches.filter((b) => classify(b) === 'at_completion').length} near done
                      </span>
                    </button>
                    {isOpen && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                              <th className="px-3 py-1.5 font-medium">Batch #</th>
                              <th className="px-3 py-1.5 font-medium">Batch / Course</th>
                              <th className="px-3 py-1.5 font-medium">Mode</th>
                              <th className="px-3 py-1.5 font-medium">Type</th>
                              <th className="px-3 py-1.5 font-medium">Start → End</th>
                              <th className="px-3 py-1.5 font-medium text-right">Students</th>
                              <th className="px-3 py-1.5 font-medium">Topic %</th>
                              <th className="px-3 py-1.5 font-medium">WhatsApp</th>
                              <th className="px-3 py-1.5 font-medium">Status</th>
                              <th className="px-3 py-1.5 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.batches.map((b) => {
                              const c = classify(b);
                              const overdueDays = c === 'past_end' && b.end_date ? daysBetween(b.end_date, todayIso()) : null;
                              return (
                                <tr key={b.batch_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`bp-row-${b.batch_number}`}>
                                  <td className="px-3 py-1.5 font-semibold text-slate-700 tabular-nums">#{b.batch_number}</td>
                                  <td className="px-3 py-1.5">
                                    <p className="text-slate-800 truncate max-w-[240px]" title={b.batch_name}>{b.batch_name}</p>
                                    <p className="text-[10px] text-slate-400 truncate max-w-[240px]" title={b.course_name}>{b.course_name}</p>
                                  </td>
                                  <td className="px-3 py-1.5 text-slate-500">{b.training_mode || '—'}</td>
                                  <td className="px-3 py-1.5">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isWeekend(b.batch_type) ? 'bg-rose-50 text-rose-700 border border-rose-100' : 'bg-blue-50 text-blue-700 border border-blue-100'}`}>
                                      {isWeekend(b.batch_type) ? 'Weekend' : 'Weekday'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">
                                    {b.start_date || '—'} <span className="text-slate-300">→</span> {b.end_date || '—'}
                                    {overdueDays != null && (
                                      <span className="ml-1 text-[10px] text-rose-600 font-semibold" data-testid={`bp-overdue-${b.batch_number}`}>+{overdueDays}d</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{b.student_count ?? 0}</td>
                                  <td className="px-3 py-1.5"><PctBar value={b.topic_completion_pct} /></td>
                                  <td className="px-3 py-1.5">
                                    {b.whatsapp_link_crm || b.whatsapp_link_manual ? (
                                      <div className="inline-flex items-center gap-1">
                                        <a href={b.whatsapp_link_crm || b.whatsapp_link_manual} target="_blank" rel="noopener noreferrer"
                                          title={[
                                            b.whatsapp_link_crm ? `CRM: ${b.whatsapp_link_crm}` : '',
                                            b.whatsapp_link_manual ? `Manual: ${b.whatsapp_link_manual}` : '',
                                            b.audit_trainer_name ? `Audit Trainer: ${b.audit_trainer_name}` : '',
                                            b.notes_of_auditor ? `Notes: ${b.notes_of_auditor}` : '',
                                          ].filter(Boolean).join('\n')}
                                          className="inline-flex items-center gap-1 text-[10px] text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded px-1.5 py-0.5"
                                          data-testid={`bp-wa-link-${b.batch_number}`}>
                                          <MessageSquare className="w-2.5 h-2.5" />
                                          {b.whatsapp_link_crm ? 'CRM' : 'Manual'}
                                        </a>
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <button
                                              className="text-slate-400 hover:text-emerald-600 p-0.5 rounded"
                                              data-testid={`bp-wa-info-${b.batch_number}`}
                                              title="View WhatsApp link details"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <Info className="w-3 h-3" />
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent align="start" className="w-80 p-3 text-[11px] space-y-2">
                                            {b.whatsapp_link_crm && (
                                              <div>
                                                <p className="text-[9px] uppercase tracking-wide text-emerald-700 font-semibold mb-0.5">CRM link</p>
                                                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded px-1.5 py-1">
                                                  <span className="truncate text-slate-700 flex-1" title={b.whatsapp_link_crm}>{b.whatsapp_link_crm}</span>
                                                  <button onClick={() => copyToClipboard(b.whatsapp_link_crm)}
                                                    className="text-slate-400 hover:text-violet-600" title="Copy" data-testid={`bp-wa-copy-crm-${b.batch_number}`}>
                                                    <Copy className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              </div>
                                            )}
                                            {b.whatsapp_link_manual && (
                                              <div>
                                                <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Manual link (trainer)</p>
                                                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded px-1.5 py-1">
                                                  <span className="truncate text-slate-700 flex-1" title={b.whatsapp_link_manual}>{b.whatsapp_link_manual}</span>
                                                  <button onClick={() => copyToClipboard(b.whatsapp_link_manual)}
                                                    className="text-slate-400 hover:text-violet-600" title="Copy" data-testid={`bp-wa-copy-manual-${b.batch_number}`}>
                                                    <Copy className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              </div>
                                            )}
                                            {(b.audit_trainer_name || b.notes_of_auditor) && (
                                              <div className="pt-1 border-t border-slate-100">
                                                {b.audit_trainer_name && <p><span className="text-slate-400">Audit Trainer:</span> <span className="text-slate-700 font-medium">{b.audit_trainer_name}</span></p>}
                                                {b.notes_of_auditor && <p className="text-slate-600 mt-0.5"><span className="text-slate-400">Notes:</span> {b.notes_of_auditor}</p>}
                                              </div>
                                            )}
                                          </PopoverContent>
                                        </Popover>
                                      </div>
                                    ) : (
                                      <span className="text-[10px] text-slate-300 italic" data-testid={`bp-wa-missing-${b.batch_number}`}>—</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5"><StatusPill status={c} /></td>
                                  <td className="px-3 py-1.5">
                                    <button onClick={() => setFillBatch(b)}
                                      data-testid={`bp-fill-pdp-mock-${b.batch_number}`}
                                      title={`Fill PDP / Mock / Video-Transcript for #${b.batch_number}`}
                                      className="text-[10px] px-1.5 py-0.5 rounded border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 inline-flex items-center gap-1">
                                      <Video className="w-2.5 h-2.5" />Fill
                                      {(b.missing_pdp_count > 0 || b.missing_mock_count > 0 || b.missing_link_count > 0) && (
                                        <span className="tabular-nums text-rose-600 font-semibold">
                                          {Math.max(b.missing_pdp_count || 0, b.missing_mock_count || 0, b.missing_link_count || 0)}
                                        </span>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 pt-2 text-[10px] text-slate-400 flex items-center justify-between">
          <span>Showing <span className="font-semibold text-slate-600">{filtered.length}</span> of {allBatches.length} batches</span>
          <span>Threshold for &quot;At Completion&quot;: Topic % ≥ {AT_COMPLETION_PCT}</span>
        </div>
      </DialogContent>
      {/* Rendered OUTSIDE DialogContent so closing the nested modal does not
          collapse the parent's overlay (was causing a white screen). */}
      <BatchPdpMockModal open={!!fillBatch} onOpenChange={(o) => !o && setFillBatch(null)} batch={fillBatch} />
    </Dialog>
  );
};

export default BatchProgressModal;
